import React, { useEffect, useMemo, useState } from 'react';
import { PencilLine, Star, User } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { formatAsteriskBoldToHtml } from '../utils/emphasis';
import { decodeEscapedHtml, sanitizeHtml } from '../utils/html';

export interface SnapshotProps {
  title: string;
  bullets: Array<{ label: string; body: string }>;
  bodyHtml?: string;
  draftHtml?: string;
  onDraftHtmlChange?: (html: string) => void;
  onCancelDraft?: () => void;
  saveCommitId?: number;
}

function escapeHtml(text: string) {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function bulletsToHtml(bullets: Array<{ label: string; body: string }>) {
  const lis = bullets
    .map((b) => {
      const label = escapeHtml(b.label);
      const body = formatAsteriskBoldToHtml(b.body);
      if (!label) return `<li>${body}</li>`;
      return `<li><strong>${label}:</strong> ${body}</li>`;
    })
    .join('');
  return `<ul>${lis}</ul>`;
}

export default function Snapshot({
  title,
  bullets,
  bodyHtml,
  draftHtml,
  onDraftHtmlChange,
  onCancelDraft,
  saveCommitId,
}: SnapshotProps) {
  // Stored HTML can arrive entity-escaped; decode at the boundary so both the display
  // and the editor receive real HTML (BPP-226). Drafts are Quill output, already clean.
  const initialHtml = useMemo(
    () => (bodyHtml ? decodeEscapedHtml(bodyHtml) : bulletsToHtml(bullets)),
    [bodyHtml, bullets]
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editorHtml, setEditorHtml] = useState<string>('');

  const displayHtml = draftHtml ?? initialHtml;

  const startEditing = () => {
    setEditorHtml(displayHtml);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    onCancelDraft?.();
    setIsEditing(false);
    setEditorHtml('');
  };

  const finishEditing = () => {
    setIsEditing(false);
  };

  useEffect(() => {
    setIsEditing(false);
    setEditorHtml('');
  }, [saveCommitId]);

  return (
    <div className="w-full rounded-[12px] bg-white">
      <div className="pt-[18px]">
        <div className="flex items-start justify-between pr-[10px]">
          <div className="flex items-start gap-[14px]">
            <div className="relative mt-[1px] size-[26px] shrink-0">
              <div className="absolute left-0 top-0">
                <User size={26} color="#25ADE3" />
              </div>
              <div className="absolute -right-[2px] -bottom-[2px]">
                <Star size={12} color="#25ADE3" fill="#25ADE3" />
              </div>
            </div>

            <p
              className="font-['Roboto_Slab',serif] text-[24px] font-bold leading-[36px] text-[#132543]"
              style={{ fontVariationSettings: "'wdth' 100", letterSpacing: '0.75px' }}
            >
              {title}
            </p>
          </div>

        {!isEditing && (
          <button
            type="button"
            className="inline-flex items-center gap-[6px] font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] text-[#53BEE8]"
            onClick={startEditing}
            data-pdf-exclude
          >
            <PencilLine size={16} color="#53BEE8" />
            Edit
          </button>
        )}
        </div>
      </div>

      <div
        className="pb-[18px] pl-[0px] pr-[10px] pt-[14px] font-['Open_Sans',sans-serif] text-[16px] font-normal leading-[22.8px] text-[#31394C]"
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        {!isEditing && (
          <div
            className="ql-snow font-normal snapshot-display"
            style={{ fontVariationSettings: "'wdth' 100" }}
            dangerouslySetInnerHTML={{ __html: `<div class="ql-editor">${sanitizeHtml(displayHtml)}</div>` }}
          />
        )}

        {isEditing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1" />
              <button
                type="button"
                className="px-3 py-1 rounded bg-[#132543] text-white"
                onClick={finishEditing}
              >
                Save
              </button>
              <button
                type="button"
                className="px-3 py-1 rounded border border-gray-200 text-[#132543]"
                onClick={cancelEditing}
              >
                Cancel
              </button>
            </div>

            <div className="snapshot-quill bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <ReactQuill
                theme="snow"
                value={editorHtml}
                onChange={(value) => {
                  setEditorHtml(value);
                  onDraftHtmlChange?.(value);
                }}
                modules={{
                  toolbar: [[{ header: [1, 2, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
